// Pure helpers for the layout-config status-tile panel: matching a flattened
// status key against the device's `status.fields` patterns and flattening nested
// status into dotted-path entries. These are dependency-free (no React/i18n) so
// they can be unit-tested in isolation; DeviceStatusDetails.tsx owns the
// React/Redux/i18n rendering on top of them.

export type StatusField = {
  label?: string;
  unit?: 'temperature' | 'current';
  omit?: boolean;
};

export type StatusFields = Record<string, StatusField>;

// Match a flattened status key against a statusFields pattern. A pattern matches
// when it (a) equals the key, (b) is an ancestor path (`temperatureSensors`
// matches the whole subtree), or (c) uses `*` as a single-segment wildcard
// (`temperatureSensors.*.temperature` matches every sensor's temperature).
// Returns the segments captured by each `*` (for label interpolation), or null
// when the pattern does not match.
export const capturePattern = (key: string, pattern: string): string[] | null => {
  if (!pattern.includes('*')) {
    return (key === pattern || key.startsWith(`${pattern}.`)) ? [] : null;
  }
  const source = pattern
    .split('.')
    .map((segment) => segment === '*' ? '([^.]+)' : segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('\\.');
  const match = new RegExp(`^${source}$`).exec(key);
  return match ? match.slice(1) : null;
};

// Resolve the statusFields descriptor for a flattened key. Prefers an exact-key
// entry over a wildcard pattern, and returns the `*` captures alongside it.
export const matchStatusField = (key: string, statusFields: StatusFields): { field: StatusField | null; captures: string[] } => {
  if (statusFields[key]) {
    return { field: statusFields[key], captures: [] };
  }
  const pattern = Object.keys(statusFields).find((candidate) => capturePattern(key, candidate));
  if (pattern) {
    return { field: statusFields[pattern], captures: capturePattern(key, pattern) ?? [] };
  }
  return { field: null, captures: [] };
};

// Build the i18n interpolation variables from a pattern's `*` captures. The first
// capture is exposed as {{sensorNumber}} to match the existing temperatureSensors.*
// translations. We intentionally do NOT expose numeric positional names ({{0}}...):
// the interpolator builds `new RegExp(`{{${name}}}`)`, and a numeric name makes
// `{{0}}` a regex quantifier that matches a stray `}` and corrupts the string.
export const captureVariables = (captures: string[]): Record<string, string> => {
  const variables: Record<string, string> = {};
  if (captures.length) { variables.sensorNumber = captures[0]; }
  return variables;
};

// Recursively flatten nested objects/arrays into dotted-path key/value pairs.
export const flattenEntries = (value: any, prefix = ''): Array<[string, any]> => {
  if (value === null || typeof value !== 'object') {
    return [[prefix, value]];
  }
  return Object.entries(value).reduce((acc: Array<[string, any]>, [key, nested]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    return acc.concat(flattenEntries(nested, path));
  }, []);
};
